import styled from 'styled-components';
import { Badge } from '~/components/ui/Badge';

export type NormalizedChoice = {
  index: number;
  text: string;
  is_correct: boolean;
};

export type NormalizedQuestion = {
  question_id: number;
  type: string;
  status?: string;
  text: string;
  choices: NormalizedChoice[];
  user_answer: string | boolean[] | string[] | null;
  has_answer: boolean;
  is_correct: boolean;
  feedback?: string | null;
};

export function QuestionAnswerView({
  question,
  index,
}: {
  question: NormalizedQuestion;
  index: number;
}) {
  const isDraft = question.status === 'draft' || question.status === 'DRAFT';
  return (
    <QuestionContainer>
      <QuestionHeader>
        <QuestionTitle>
          {index}. {question.text || <em>(no question text)</em>}
        </QuestionTitle>
        <BadgeRow>
          {isDraft && <Badge $variant="warning">Draft</Badge>}
          {question.has_answer ? (
            <Badge $variant={question.is_correct ? 'success' : 'error'}>
              {question.is_correct ? 'Correct' : 'Incorrect'}
            </Badge>
          ) : (
            <Badge $variant="neutral">No answer</Badge>
          )}
        </BadgeRow>
      </QuestionHeader>

      {!question.has_answer ? (
        <NoAnswerBlock>
          The student did not submit an answer for this question.
        </NoAnswerBlock>
      ) : question.type === 'choose_one' ||
        question.type === 'choose_multiple' ? (
        <MultipleChoiceView
          choices={question.choices}
          userAnswer={
            Array.isArray(question.user_answer)
              ? (question.user_answer as boolean[])
              : null
          }
        />
      ) : question.type === 'essay' ? (
        <EssayView
          answer={
            typeof question.user_answer === 'string' ? question.user_answer : ''
          }
        />
      ) : (
        <FillInBlankView
          answer={
            Array.isArray(question.user_answer)
              ? (question.user_answer as string[])
              : []
          }
        />
      )}

      {question.feedback ? (
        <FeedbackBlock>
          <FeedbackLabel>Grader feedback</FeedbackLabel>
          <FeedbackText>{question.feedback}</FeedbackText>
        </FeedbackBlock>
      ) : null}
    </QuestionContainer>
  );
}

function MultipleChoiceView({
  choices,
  userAnswer,
}: {
  choices: NormalizedChoice[];
  userAnswer: boolean[] | null;
}) {
  return (
    <ChoiceList>
      {choices.map((choice) => {
        const selected = userAnswer ? Boolean(userAnswer[choice.index]) : false;
        const correct = choice.is_correct;
        const variant: 'correct' | 'wrong' | 'missed' | 'plain' = correct
          ? selected
            ? 'correct'
            : 'missed'
          : selected
            ? 'wrong'
            : 'plain';
        return (
          <ChoiceRow key={choice.index} $variant={variant}>
            <ChoiceMark>
              {selected ? (correct ? '✓' : '✗') : correct ? '·' : ''}
            </ChoiceMark>
            <ChoiceText>{choice.text}</ChoiceText>
            <ChoiceTags>
              {selected && <small>student selected</small>}
              {correct && <small>correct</small>}
            </ChoiceTags>
          </ChoiceRow>
        );
      })}
    </ChoiceList>
  );
}

function EssayView({ answer }: { answer: string }) {
  return <AnswerBlock>{answer ? answer : <em>(no answer)</em>}</AnswerBlock>;
}

function FillInBlankView({ answer }: { answer: string[] }) {
  if (!answer.length) {
    return (
      <AnswerBlock>
        <em>(no answer)</em>
      </AnswerBlock>
    );
  }
  return (
    <ChoiceList>
      {answer.map((value, i) => (
        <ChoiceRow key={i} $variant="plain">
          <ChoiceMark>{i + 1}.</ChoiceMark>
          <ChoiceText>{value || <em>(blank)</em>}</ChoiceText>
        </ChoiceRow>
      ))}
    </ChoiceList>
  );
}

const QuestionContainer = styled.div`
  padding: var(--inner-padding);
  border-bottom: var(--component-border);

  &:last-child {
    border-bottom: none;
  }
`;

const QuestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 0.75rem;
`;

const QuestionTitle = styled.div`
  font-weight: 600;
  flex: 1;
`;

const ChoiceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ChoiceRow = styled.div<{
  $variant: 'correct' | 'wrong' | 'missed' | 'plain';
}>`
  display: grid;
  grid-template-columns: 1.5rem 1fr auto;
  gap: 0.5rem;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  border-left: 3px solid
    ${(p) =>
      p.$variant === 'correct' || p.$variant === 'missed'
        ? 'var(--success)'
        : p.$variant === 'wrong'
          ? 'var(--error)'
          : 'transparent'};
  background: ${(p) =>
    p.$variant === 'correct'
      ? 'color-mix(in srgb, var(--success) 14%, transparent)'
      : p.$variant === 'wrong'
        ? 'color-mix(in srgb, var(--error) 14%, transparent)'
        : p.$variant === 'missed'
          ? 'color-mix(in srgb, var(--success) 6%, transparent)'
          : 'transparent'};
`;

const ChoiceMark = styled.span`
  font-weight: 700;
  text-align: center;
`;

const ChoiceText = styled.span``;

const ChoiceTags = styled.span`
  display: flex;
  gap: 0.5rem;
  color: var(--grey-600);
`;

const AnswerBlock = styled.div`
  white-space: pre-wrap;
  padding: 0.75rem;
  background: var(--grey-75);
  border-left: 3px solid var(--grey-300);
  border-radius: 4px;
`;

const NoAnswerBlock = styled.div`
  padding: 0.75rem;
  background: var(--grey-75);
  border-left: 3px dashed var(--grey-450);
  border-radius: 4px;
  color: var(--grey-700);
  font-style: italic;
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const FeedbackBlock = styled.div`
  margin-top: 0.75rem;
  padding: 0.75rem;
  background: color-mix(in srgb, var(--blue) 8%, transparent);
  border-left: 3px solid var(--blue);
  border-radius: 4px;
`;

const FeedbackLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--blue);
  margin-bottom: 0.25rem;
`;

const FeedbackText = styled.div`
  white-space: pre-wrap;
`;
